import type { HttpErrorResponse } from '@angular/common/http';
import { inject, InjectionToken } from '@angular/core';

import type { StoreConfig } from '@ngrx/store';
import { createSelector } from '@ngrx/store';

import type { CurrentUser } from '@recruitee/account-types';
import { ADMIN } from '@recruitee/common';
import type { FeatureDiscoveryFeatures } from '@recruitee/feature-discovery-types';
import type { SingleEntityState } from '@recruitee/ngrx';
import { createSingleEntityStore, memoizeSelector, pipeState } from '@recruitee/ngrx';

import type { UserFormData, UserPasswordFormData } from './current-user.types';
import { EntityStoreKey } from '../../types';

const { actions, selectors, reducer, namespace } = createSingleEntityStore<CurrentUser>()(
  EntityStoreKey.account,
  {
    customActions: adapter => ({
      update: (state, payload: UserFormData) => adapter.markAsPending(state),
      updatePassword: (state, payload: UserPasswordFormData) => adapter.markAsPending(state),
      updatePasswordSuccess: (state, payload: CurrentUser) =>
        pipeState(
          state,
          next => adapter.save(next, payload),
          next => adapter.markAsLoaded(next),
        ),
      updatePasswordFailed: (state, payload: HttpErrorResponse) => adapter.setError(state, payload),
      updateFeatureDiscovery: (state, payload: FeatureDiscoveryFeatures) =>
        adapter.update(state, { featureDiscovery: payload }),
    }),
  },
);

export const CurrentUserConfigToken = new InjectionToken('CurrentUserConfigToken', {
  providedIn: 'root',
  factory: (): StoreConfig<SingleEntityState<CurrentUser>> => ({
    initialState: {
      loaded: true,
      failed: false,
      pending: false,
      error: false,
      data: inject(ADMIN),
    },
  }),
});

const selectFeatureDiscovery = memoizeSelector(
  ({ feature }: { feature: keyof CurrentUser['featureDiscovery'] }) =>
    createSelector(selectors.data, function featureDiscovery(currentUser: CurrentUser) {
      const featureDiscovery = currentUser?.featureDiscovery;
      if (!featureDiscovery) return false;
      return featureDiscovery[feature];
    }),
);

const selectOnboardingVideo = createSelector(
  selectors.data,
  function onboardingVideo(currentUser: CurrentUser) {
    return currentUser?.onboardingVideos;
  },
);

const CurrentUserSelectors = {
  ...selectors,
  featureDiscovery: selectFeatureDiscovery,
  onboardingVideos: selectOnboardingVideo,
};

export {
  actions as CurrentUserEntityActions,
  CurrentUserSelectors,
  reducer as CurrentUserReducer,
  namespace as CurrentUserNamespace,
};
